import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { categories, topics } from "@/mocks/home";

type VoteState = {
  a: number;
  b: number;
  picked: "a" | "b" | null;
};

function formatHeat(heat: number) {
  return heat >= 10000 ? `${(heat / 10000).toFixed(1)}万` : `${heat}`;
}

export default function TopicFeed() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [votes, setVotes] = useState<Record<string, VoteState>>({});

  const list = useMemo(
    () => (activeCategory === "all" ? topics : topics.filter((t) => t.category === activeCategory)),
    [activeCategory]
  );

  const getVote = (id: string, baseA: number, baseB: number): VoteState =>
    votes[id] ?? { a: baseA, b: baseB, picked: null };

  const handleVote = (id: string, side: "a" | "b", baseA: number, baseB: number) => {
    setVotes((prev) => {
      const current = prev[id] ?? { a: baseA, b: baseB, picked: null };
      if (current.picked === side) return prev;
      const next: VoteState = {
        a: current.a + (side === "a" ? 1 : 0) - (current.picked === "a" ? 1 : 0),
        b: current.b + (side === "b" ? 1 : 0) - (current.picked === "b" ? 1 : 0),
        picked: side,
      };
      return { ...prev, [id]: next };
    });
  };

  return (
    <section id="topics" className="w-full scroll-mt-24 bg-background-100/60 py-14 md:py-20" aria-labelledby="topics-title">
      <div className="mx-auto w-full max-w-[1400px] px-4 md:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-accent-700">
              <i className="ri-flashlight-line text-sm" aria-hidden="true" />
              TOPIC FEED · 瓜田热榜
            </span>
            <h2 id="topics-title" className="mt-3 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
              今日瓜田热榜
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              每个话题都给了两派立场，看完直接投票站队。票数是瓜友们用脚投出来的，不代表任何官方结论。
            </p>
          </div>
          <p className="shrink-0 text-sm text-foreground-500">
            共 <span className="font-bold text-primary-600">{list.length}</span> 个话题
          </p>
        </div>

        <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto pb-1">
          {categories.map((cat) => {
            const active = cat.id === activeCategory;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {cat.name}
                <span className={active ? "text-background-100/80" : "text-foreground-400"}>{cat.count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {list.map((topic) => {
            const vote = getVote(topic.id, topic.votesA, topic.votesB);
            const total = vote.a + vote.b;
            const percentA = Math.round((vote.a / total) * 100);
            return (
              <article
                key={topic.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative h-[180px] w-full overflow-hidden">
                  <img
                    src={topic.cover}
                    alt={`${topic.title} - 网爆黑料话题配图`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-bold text-foreground-800">
                    {topic.categoryName}
                  </span>
                  {topic.hot && (
                    <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50">
                      <i className="ri-fire-fill text-xs" aria-hidden="true" />
                      爆
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line text-sm text-primary-500" aria-hidden="true" />
                      {formatHeat(topic.heat)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line text-sm" aria-hidden="true" />
                      {topic.commentCount}
                    </span>
                    <span>{topic.time}</span>
                  </div>

                  <Link to={`/topic/${topic.id}`} className="mt-3 block">
                    <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                      {topic.title}
                    </h3>
                  </Link>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {topic.tags.map((tag) => (
                      <span key={tag} className="rounded-md bg-secondary-50 px-2 py-1 text-xs text-secondary-800">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto pt-5">
                    <div className="flex items-center justify-between text-xs font-medium text-foreground-500">
                      <span>{topic.sideA}</span>
                      <span>{topic.sideB}</span>
                    </div>
                    <div className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full bg-background-200">
                      <span
                        className="h-full origin-left animate-bar-grow bg-primary-500"
                        style={{ width: `${percentA}%` }}
                      />
                      <span className="h-full bg-secondary-400" style={{ width: `${100 - percentA}%` }} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleVote(topic.id, "a", topic.votesA, topic.votesB)}
                        aria-pressed={vote.picked === "a"}
                        className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2.5 text-xs font-bold transition-colors ${
                          vote.picked === "a"
                            ? "bg-primary-500 text-background-50"
                            : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                        }`}
                      >
                        <i className="ri-thumb-up-line text-base" aria-hidden="true" />
                        {topic.sideA}
                        <span className="text-foreground-400">{percentA}%</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleVote(topic.id, "b", topic.votesA, topic.votesB)}
                        aria-pressed={vote.picked === "b"}
                        className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2.5 text-xs font-bold transition-colors ${
                          vote.picked === "b"
                            ? "bg-secondary-500 text-background-50"
                            : "border border-background-300 text-foreground-700 hover:border-secondary-400 hover:text-secondary-700"
                        }`}
                      >
                        <i className="ri-thumb-up-line text-base" aria-hidden="true" />
                        {topic.sideB}
                        <span className="text-foreground-400">{100 - percentA}%</span>
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}